import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Users,
  HelpCircle,
  CheckCircle2,
  Clock,
  XCircle,
  ShieldOff,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  AlertTriangle,
} from "lucide-react";
import { KPICard, pct } from "./KPICard";
import { SectionHeader } from "./HelperText";
import { useEmailDeliverabilityData } from "../../hooks/useEmailDeliverabilityData";
import { useEmailHygieneReconciliationActiveCount } from "../../hooks/useEmailHygieneReconciliationActiveCount";
import { useEmailRecipientDndData } from "../../hooks/useEmailRecipientDndData";

// Checkpoint 3.5A. Authoritative vocabulary only — verified against
// backend/app/integrations/sendgrid/enums.py (EmailHygieneState,
// ProductionEligibility) during characterization, not invented marketing
// terminology. See emailDeliverabilityMockAdapter.js for the full
// source-of-truth rationale (why GHL projection field values are NOT used
// here).
const HYGIENE_STATE_DEFS = [
  { key: "untested", label: "Untested", icon: HelpCircle, barClass: "bg-slate-400" },
  { key: "deliveryConfirmed", label: "Delivery Confirmed", icon: CheckCircle2, barClass: "bg-emerald-400" },
  { key: "temporarilyUndeliverable", label: "Temporarily Undeliverable", icon: Clock, barClass: "bg-amber-400" },
  { key: "invalid", label: "Invalid", icon: XCircle, barClass: "bg-red-400" },
  { key: "suppressed", label: "Suppressed", icon: ShieldOff, barClass: "bg-purple-400" },
  // DASH-EMAIL-007: EmailHygieneState's real sixth value -- distinct from
  // "suppressed" (consent/compliance, sticky) and never merged with it:
  // provider-side refusal with insufficient evidence for permanent
  // invalidity, non-sticky/recoverable (see backend/app/integrations/
  // sendgrid/enums.py's own EmailHygieneState.PROVIDER_SUPPRESSED
  // comment). The grid below wraps to a second row for this 7th tile
  // (6 states + 1 total) under the existing lg:grid-cols-6 -- an
  // additive completion of real enum coverage, not a layout redesign.
  { key: "providerSuppressed", label: "Provider Suppressed", icon: ShieldOff, barClass: "bg-fuchsia-400" },
];

// Checkpoint 3.5A remediation: "Total Recipients" was replaced with
// "Recipient Records" — an adversarial review found the former could
// imply a unique-human count. ADR-0005 keys SendGridRecipientState by
// account_id + environment + normalized_recipient_email, so the same
// person can legitimately hold a separate record in Qualification and in
// Production; under Environment=All this total is their additive union,
// not deduplicated. "Records" is accurate without requiring a caveat on
// every card — the explanatory sentence lives once, in the section
// helper text below, and only when Environment=All (see SectionHeader).
//
// DASH-EMAIL-006a (MOCK-1): Audience's own "Recipient Records" KPI
// (EmailAudienceView.jsx) is a DIFFERENT, independent mock population
// (segmentation/lifecycle-state model) that does not reconcile with this
// view's hygiene-tracked total under the same Environment filter — both
// counts are legitimate on their own terms, but sharing an identical,
// unqualified label risked implying they describe the same universe.
// SECTION_HELPER (below) now always states which population this view
// counts, regardless of the Environment selection, so the two views'
// numbers are never silently compared as if interchangeable.
const SUMMARY_KPI_DEFS = [
  { key: "__total", label: "Recipient Records", icon: Users },
  ...HYGIENE_STATE_DEFS,
];

const RECIPIENT_RECORDS_SCOPE_NOTE =
  "Recipient Records here reflect hygiene/deliverability-tracked SendGrid recipient state (EmailHygieneState) — a different, independently-tracked count from the Audience view's segmented recipient records.";

// DASH-EMAIL-007: this view's hygiene/eligibility grid is now backed by a
// real, read-only RECIPIENT-grain aggregation over
// sendgrid_hygiene_recipient_state (see useEmailDeliverabilityData.js) —
// "exact" is the Metric Confidence Standard's real/high-confidence status
// (see KPICard.jsx's confidenceLabels), replacing the prior shell phase's
// "estimated" mock treatment. This is a genuine current-state count, not
// an estimate — the reason string says so precisely rather than reusing
// the old mock disclosure text, which would now be false.
const HYGIENE_METRIC_REASON =
  "Live current-state count from SendGrid recipient hygiene state (sendgrid_hygiene_recipient_state), scoped to your account.";

const ELIGIBILITY_DEFS = [
  { key: "eligible", label: "Eligible", icon: ShieldCheck, barClass: "bg-emerald-400" },
  { key: "notEligible", label: "Not Eligible", icon: ShieldAlert, barClass: "bg-red-400" },
  { key: "unknown", label: "Unknown", icon: HelpCircle, barClass: "bg-slate-400" },
];

function DeliverabilitySkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="deliverability-skeleton">
      {SUMMARY_KPI_DEFS.map((def) => (
        <Card key={def.key} className="glass border-white/10 animate-pulse">
          <CardContent className="p-4 space-y-3">
            <div className="h-3 w-20 rounded bg-white/10" />
            <div className="h-6 w-16 rounded bg-white/10" />
            <div className="h-3 w-24 rounded bg-white/5" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Small, local proportional-width bar list — reuses EmailFunnelCard's bar
// TECHNIQUE (Card > per-row label/count/bar), not the component itself:
// EmailFunnelCard's down-arrow connectors and "% of prev" framing imply
// sequential funnel progression, which is semantically wrong here (a
// recipient occupies exactly one of five mutually-exclusive states, not a
// multi-stage progression through all five).
function StateDistributionBars({ rows, total }) {
  const maxValue = Math.max(...rows.map((r) => r.value || 0), 1);
  return (
    <div className="space-y-3">
      {rows.map((row) => {
        const width = Math.max((row.value / maxValue) * 100, row.value > 0 ? 4 : 0);
        const share = total ? row.value / total : 0;
        const Icon = row.icon;
        return (
          <div key={row.key} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-slate-200">
                <Icon className="h-3.5 w-3.5 text-slate-400" />
                {row.label}
              </span>
              <div className="flex items-center gap-2">
                <span className="font-mono text-slate-100">{row.value.toLocaleString()}</span>
                <span className="text-xs text-slate-400 w-10 text-right">{pct(share)}</span>
              </div>
            </div>
            <div className="h-2.5 w-full rounded-full bg-white/5 overflow-hidden">
              <div className={`h-full rounded-full ${row.barClass} transition-all duration-500`} style={{ width: `${width}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EnvironmentBreakdownRow({ row, isHighlighted }) {
  const total = row.total || 1;
  return (
    <div
      className={`rounded-lg border p-3 ${
        isHighlighted ? "border-indigo-400/50 bg-indigo-500/5" : "border-white/10 bg-white/[0.02]"
      }`}
      data-testid={`deliverability-environment-row-${row.environment}`}
    >
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium capitalize text-slate-100">{row.environment}</span>
        <span className="font-mono text-slate-100">{row.total.toLocaleString()}</span>
      </div>
      <div className="mt-2 flex h-2 w-full overflow-hidden rounded-full bg-white/5">
        {HYGIENE_STATE_DEFS.map((def) => (
          <div
            key={def.key}
            className={def.barClass}
            style={{ width: `${((row.hygiene[def.key] || 0) / total) * 100}%` }}
            title={`${def.label}: ${(row.hygiene[def.key] || 0).toLocaleString()}`}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * EmailDeliverabilityView — Checkpoint 3.5A / DASH-EMAIL-007.
 *
 * The Email tab's "Deliverability & Hygiene" view — an operational
 * Recipient Health surface (current-state / as-of-now inventory), built
 * against the authoritative EmailHygieneState / SendGridEnvironment /
 * ProductionEligibility vocabulary rather than downstream GHL projection
 * fields. As of DASH-EMAIL-007, the hygiene/eligibility grid is real,
 * read-only RECIPIENT-grain data (useEmailDeliverabilityData.js ->
 * sendgrid_hygiene_recipient_state) — the Reconciliation Required tile
 * remains its own, separately-sourced real data (DASH-EMAIL-013), and the
 * two are never blended. Deliberately does NOT include historical trend/
 * movement, per-recipient tables, or a GHL-projection reconciliation diff
 * — those require a backend contract that does not exist yet.
 *
 * Consumes only the APPLIED Environment value (never draft) — mirrors
 * EmailOverviewView/EmailPerformanceView's existing contract. Period,
 * Broadcast, Program, and Campaign are not accepted: none has a defined
 * meaning against SendGridRecipientState today.
 */
export default function EmailDeliverabilityView({ environment, onOpenReconciliation }) {
  const { loading, error, total, hygiene, eligibility, eligibilityRate, environmentBreakdown } =
    useEmailDeliverabilityData({ environment });

  // DASH-EMAIL-013: real Email Hygiene Reconciliation queue total
  // (RECON-002's own list API `total`, via `limit=1` — see that hook's own
  // comment), replacing the mock adapter's `reconciliationRequired` this
  // card previously displayed. Fetched independently of the rest of this
  // (still mock-backed) view, so a real-API failure here never blocks the
  // rest of the Recipient Health summary from rendering.
  const reconciliationCount = useEmailHygieneReconciliationActiveCount({ environment });

  // DASH-EMAIL-010: real, independently-fetched RECIPIENT-grain DND data
  // from email_dnd_current_state -- a STRICTLY SEPARATE state machine from
  // this view's hygiene grain above (sendgrid_hygiene_recipient_state).
  // Fetched independently so a DND-fetch failure never blocks the rest of
  // this (hygiene) view, and vice versa. See useEmailRecipientDndData.js's
  // own doc comment for the never-fabricate-on-failure contract.
  const dnd = useEmailRecipientDndData();

  const hasData = hygiene && eligibility;

  return (
    <div className="space-y-6" data-testid="email-deliverability-view">
      {error && (
        <Card className="glass border-red-500/20" data-testid="deliverability-error">
          <CardContent className="p-4 text-sm text-red-300">Failed to load Deliverability data: {error}</CardContent>
        </Card>
      )}

      {!error && loading && <DeliverabilitySkeleton />}

      {!error && !loading && hasData && (
        <>
          {/* A. Recipient Health Summary. The helper text only appears
              under Environment=All, where "Recipient Records" is an
              additive union across two environments — see
              SUMMARY_KPI_DEFS's own comment. */}
          <div>
            <SectionHeader
              title="Recipient Health Summary"
              helper={
                environment === "all"
                  ? `${RECIPIENT_RECORDS_SCOPE_NOTE} Records are also environment-scoped — the same recipient may appear separately in Qualification and Production.`
                  : RECIPIENT_RECORDS_SCOPE_NOTE
              }
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="deliverability-summary-grid">
              {SUMMARY_KPI_DEFS.map((def) => (
                <KPICard
                  key={def.key}
                  title={def.label}
                  value={def.key === "__total" ? total : (hygiene[def.key] ?? 0)}
                  format="number"
                  icon={def.icon}
                  status="exact"
                  reason={HYGIENE_METRIC_REASON}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* B. Production Eligibility */}
            <Card className="glass border-white/10" data-testid="deliverability-eligibility-section">
              <CardHeader>
                <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-indigo-400" />
                  Production Eligibility
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  {ELIGIBILITY_DEFS.map((def) => (
                    <KPICard
                      key={def.key}
                      title={def.label}
                      value={eligibility[def.key] ?? 0}
                      format="number"
                      icon={def.icon}
                      status="exact"
                      reason={HYGIENE_METRIC_REASON}
                    />
                  ))}
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-300">Eligibility Rate</span>
                    <span className="font-mono text-slate-100" data-testid="deliverability-eligibility-rate">
                      {pct(eligibilityRate)}
                    </span>
                  </div>
                  <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-white/5">
                    {ELIGIBILITY_DEFS.map((def) => (
                      <div
                        key={def.key}
                        className={def.barClass}
                        style={{ width: `${total ? (eligibility[def.key] / total) * 100 : 0}%` }}
                        title={`${def.label}: ${eligibility[def.key].toLocaleString()}`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Recipients currently eligible for Production, out of the current Environment selection.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* C. Recipient State Distribution */}
            <Card className="glass border-white/10" data-testid="deliverability-distribution-card">
              <CardHeader>
                <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
                  <Users className="h-5 w-5 text-indigo-400" />
                  Recipient State Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <StateDistributionBars
                  rows={HYGIENE_STATE_DEFS.map((def) => ({ ...def, value: hygiene[def.key] ?? 0 }))}
                  total={total}
                />
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* D. Environment Breakdown */}
            <Card className="glass border-white/10" data-testid="deliverability-environment-breakdown">
              <CardHeader>
                <CardTitle className="text-lg text-slate-100">Environment Breakdown</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {environmentBreakdown.map((row) => (
                  <EnvironmentBreakdownRow key={row.environment} row={row} isHighlighted={environment === row.environment} />
                ))}
              </CardContent>
            </Card>

            {/* E. Reconciliation Required — deliberately narrow: a grounded
                backlog count, nothing more. Checkpoint 3.5A remediation:
                an adversarial review found this section previously
                presented an invented Healthy/Watch/Critical severity
                system (arbitrary <=0/<=20/>20 thresholds) as if it were a
                backend-defined operational fact. P1 defines
                RecipientProcessingStatus.RECONCILIATION_REQUIRED as a
                per-recipient processing status, not a graded severity, and
                gives no contract for what count is fine vs. concerning —
                so no severity label, color, or badge is shown. No
                authoritative-vs-GHL comparison, drift count, or sync
                percentage either — nothing in the repository computes
                those today (see this checkpoint's characterization
                report). Neutral, informational styling only.

                DASH-EMAIL-013: this is now the real, honest entry point
                into the Email Hygiene Reconciliation queue
                (RECON-002/003) — the count is real backend data
                (useEmailHygieneReconciliationActiveCount), never the mock
                adapter's synthetic number, and the whole card is clickable
                only once a real count has successfully loaded. A count
                fetch failure renders an explicit "Unavailable" state, per
                this checkpoint's "never real-or-fake" data hierarchy —
                never a fabricated 0. */}
            <Card
              className={`glass border-white/10 ${!reconciliationCount.loading && !reconciliationCount.error ? "cursor-pointer transition-colors hover:border-indigo-400/40 hover:bg-white/[0.03]" : ""}`}
              data-testid="deliverability-reconciliation-card"
              onClick={
                !reconciliationCount.loading && !reconciliationCount.error
                  ? () => onOpenReconciliation?.()
                  : undefined
              }
            >
              <CardHeader>
                <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
                  <RefreshCw className="h-5 w-5 text-indigo-400" />
                  Reconciliation Required
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reconciliationCount.loading ? (
                  <div className="h-8 w-16 animate-pulse rounded bg-white/10" data-testid="deliverability-reconciliation-count-loading" />
                ) : reconciliationCount.error ? (
                  <p className="text-sm text-red-300" data-testid="deliverability-reconciliation-count-error">
                    Unavailable — {reconciliationCount.error}
                  </p>
                ) : (
                  <>
                    <p className="text-2xl font-bold text-slate-100" data-testid="deliverability-reconciliation-count">
                      {reconciliationCount.total.toLocaleString()}
                    </p>
                    {/* EMAIL-HYGIENE-GATE-3A: per-class breakdown, both
                        backend-computed (never frontend-summed) -- lets an
                        operator see at a glance whether the count is
                        native reconciliations, Production Sync failures,
                        or a mix, without a second request. */}
                    {(typeof reconciliationCount.nativeTotal === "number" || typeof reconciliationCount.propagationTotal === "number") && (
                      <p className="text-xs text-slate-400 mt-0.5" data-testid="deliverability-reconciliation-breakdown">
                        {(reconciliationCount.nativeTotal ?? 0).toLocaleString()} native ·{" "}
                        {(reconciliationCount.propagationTotal ?? 0).toLocaleString()} Production Sync
                      </p>
                    )}
                  </>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Recipients currently in reconciliation_required, pending operator review.
                  {!reconciliationCount.loading && !reconciliationCount.error && " Click to review."}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* F. Email DND (GHL Sync) -- DASH-EMAIL-010. A strictly
              separate state machine from the hygiene "Suppressed"/
              "Provider Suppressed" tiles above (sendgrid_hygiene_recipient_
              state): DASH-EMAIL-010's characterization proved no
              code-provable equivalence between "DND flagged" (last
              GHL-workflow-reported Email DND Required state) and hygiene
              suppression, so the two are never merged into one number and
              this is never labeled Unsubscribed/Suppressed/Opted Out/
              Cannot Email/Contactable. One primary tile
              (dnd_flagged_count) plus two compact subordinate lines
              (sync-health callout, cleared count) rather than three new
              full-size tiles, per this checkpoint's own layout guidance. */}
          <Card className="glass border-white/10" data-testid="deliverability-dnd-card">
            <CardHeader>
              <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
                <ShieldOff className="h-5 w-5 text-indigo-400" />
                Email DND Flagged
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dnd.loading ? (
                <div
                  className="h-8 w-16 animate-pulse rounded bg-white/10"
                  data-testid="deliverability-dnd-flagged-loading"
                />
              ) : dnd.error || dnd.evidenceStatus !== "available" || dnd.dndFlaggedCount === null ? (
                <p className="text-sm text-slate-400" data-testid="deliverability-dnd-flagged-unavailable">
                  UNAVAILABLE
                </p>
              ) : (
                <p className="text-2xl font-bold text-slate-100" data-testid="deliverability-dnd-flagged-count">
                  {dnd.dndFlaggedCount.toLocaleString()}
                </p>
              )}
              <p className="text-xs text-slate-500 mt-1">
                Last GHL-workflow-reported Email DND Required state.
              </p>

              {/* Sync-health callout -- deliberately NOT another
                  consent-state axis; distinct styling/wording so it never
                  reads as a competing DND value. */}
              {!dnd.loading && dnd.evidenceStatus === "available" && dnd.syncNeedsAttentionCount !== null && (
                <p
                  className="text-xs mt-2 flex items-center gap-1 text-amber-300"
                  data-testid="deliverability-dnd-sync-needs-attention"
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  DND Sync Needs Attention: {dnd.syncNeedsAttentionCount.toLocaleString()}
                </p>
              )}

              {/* Cleared -- compact supporting value, not a large tile
                  (the API exposes dnd_cleared_count, but this checkpoint's
                  own guidance is explicit: do not build a large tile
                  merely because the field exists). */}
              {!dnd.loading && dnd.evidenceStatus === "available" && dnd.dndClearedCount !== null && (
                <p className="text-xs text-slate-500 mt-1" data-testid="deliverability-dnd-cleared-count">
                  Cleared: {dnd.dndClearedCount.toLocaleString()}
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
